import Cookie from 'js-cookie';

/**
 * Creates new A/B tests and sets cookies, pushes data layer details to GA and removes non-selected elements.
 * A/B tests are created by adding a test class for two elements. Third or more instances of the same class will be disregarded.
 * Class structure example: "ab-test-<yourTestId>"
 *
 * @example
 * ```
 * <div class="ab-test-foobar">Variant A</div>
 * <div class="ab-test-foobar">Variant B</div>
 * ```
 *
 * AB Test works also with click events. Add data-ab-test-type="click" to the element.
 * Custom click event will be triggered on the selected variant element.
 * Variant must have event listener for "customClick" event.
 *
 * AB Test can trigger multiple variants with the same test ID.
 * @example
 * <div class="ab-test-foobar" data-ab-test-variant="a" data-ab-test-type="click">First Variant A</div>
 * <div class="ab-test-foobar" data-ab-test-variant="b" data-ab-test-type="click">First Variant B</div>
 *
 * <div class="ab-test-foobar" data-ab-test-variant="a" data-ab-test-type="click">Second Variant A</div>
 * <div class="ab-test-foobar" data-ab-test-variant="b" data-ab-test-type="click">Second Variant B</div>
 */
class ABTesting {

  static instance = null;

  constructor() {
    if (ABTesting.instance) {
      return ABTesting.instance;
    }

    ABTesting.instance = this;

    return this;
  }

  /**
   * Get test elements
   * @param {Array|String} testIds - Optional test ID to init only a specific test
   * @returns {HTMLElement}
   */
  #getTestElements(testIds) {
    let querySelector = '[class*="ab-test-"]';

    if (testIds && typeof testIds === 'string') {
      querySelector = `[class*="ab-test-${testIds}"]`;
    } else if (testIds && Array.isArray(testIds) && testIds.length) {
      const selectors = [];
      testIds.forEach((id) => {
        selectors.push(`[class*="ab-test-${id}"]`);
      });
      querySelector = selectors.join(',');
    }

    return document.querySelectorAll(querySelector);
  }

  /**
   * Create a new A/B test cookie or get variant from existing one.
   * Add details to the data layer.
   * Remove the non-selected test variant.
   * @param {Object} config - Single A/B Test configurations
   */
  #test(config) {
    const name = config.dataLayerVar;
    const cookieName = `abTest_${name}`;
    const currentCookie = String(Cookie.get(cookieName));
    const percentageOfUsers = config.percentageOfUsers;

    let expires = config.sessionDuration; //days
    let variants = config[name];
    let currentVariant;

    // Check if we can find the cookie with a valid value
    if (currentCookie) {
      for (const variant of variants) {
        if (currentCookie === String(variant.value)) {
          currentVariant = variant;
        }
      }
    }

    // If we couldn't find the cookie, create a new one with generated value
    if (!currentVariant) {
      if (typeof percentageOfUsers === 'number' && variants.length === 2) {
        const randomId = Math.floor(Math.random() * (10 / (percentageOfUsers / 10))) === 0;
        currentVariant = randomId ? variants[0] : variants[1];
      } else {
        const randomId = Math.floor(Math.random() * variants.length);
        currentVariant = variants[randomId];
      }

      const params = {};
      if (expires && expires > 0) {
        params.expires = expires;
      }

      Cookie.set(cookieName, String(currentVariant.value), params);
    }

    // Push to data layer
    const dataLayerVar = `abTest_${config.dataLayerVar || name}`;
    const dataLayerObj = {};
    dataLayerObj[dataLayerVar] = currentVariant.value;

    window.dataLayer.push(dataLayerObj);

    variants = variants.map(variant => {
      return {
        show: variant.value === currentVariant.value,
        elements: variant.elements,
      };
    });

    this.#apply(variants);
  }

  /**
   * Create the test config objects and initialize all A/B tests.
   */
  #tests() {
    Object.keys(this._abTests).forEach(testId => {
      const testConfig = {
        dataLayerVar: testId,
        sessionDuration: 30,
      };

      testConfig[testId] = Object.values(this._abTests[testId]).map((variant, index) => ({
        value: index,
        elements: Object.values(variant),
      }));

      this.#test(testConfig);
    });
  }

  /**
   * Remove the non-selected variant
   * Do nothing if null element is selected to be removed
   *
   * Note: If variant element is modal, do show element by defult.
   *       Otherwise it breaks modal functionality if modal
   *       "display" property is set to "block" via inline style
   *
   * @param {Array} variants - A/B test variants
   */
  #apply(variants) {
    variants.forEach(variant => {
      variant.elements.forEach(element => {
        if (element.dataset.abTestType === 'click') {
          // Click selected variant element
          if (element && variant.show) {
            element.dispatchEvent(new CustomEvent('customClick'));
          }
        } else {
          // Show or hide variants
          if (element && !variant.show) {
            element.remove();
          } else if (element && variant.show) {
            if(!element.classList.contains('elevated-container')) {
              element.classList.add('visible');
            }
          }
        }
      });
    });
  }

  #waitForElement(selector, callback) {
    const observer = new MutationObserver((mutations, observer) => {
      const element = document.querySelector(selector);
      if (element) {
        observer.disconnect();
        callback(element);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Create a new A/B test cookie or get variant from existing one.
   * Add details to the data layer.
   * Remove the non-selected test variant.
   *
   * @param {Array} elements - A/B test elements
   */
  #manageTestVariants(elements) {
    elements.forEach((element, index) => {
      // eslint-disable-next-line no-useless-escape
      const testIdRegex = new RegExp(/ab\-test\-(.*)/, 'g');
      const testClass = Array.from(element.classList).filter(className => testIdRegex.test(className))[0];
      const testId = testClass ? testClass.replace(testIdRegex, '$1') : false;
      const variantId = element.dataset.abTestVariant ? element.dataset.abTestVariant : index;
      const variantName = `variant-${variantId}`;

      if (testId && variantName) {
        if (!Object.prototype.hasOwnProperty.call(this._abTests, testId)) {
          this._abTests[testId] = {};
        }

        if (!Object.prototype.hasOwnProperty.call(this._abTests[testId], variantName)) {
          this._abTests[testId][variantName] = [];
        }

        this._abTests[testId][variantName].push(element);
      }
    });

    this.#tests();
  }

  /**
   * Initialize A/B tests
   * @param {Array|String} testIds - Optional test ID to init only a specific test
   */
  init(testIds) {

    let elements = [];
    this._abTests = {};

    // If page content is loaded dynamically via ajax, we need to wait
    // until the page is fully loaded, before we are fetching the test elements
    if (document.querySelector('.js-ab-test-dynamic-content')) {
      this.#waitForElement('.js-ab-test-dynamic-element', () => {
        elements = this.#getTestElements(testIds);
        this.#manageTestVariants(elements);
      });
    } else {
      elements = this.#getTestElements(testIds);
      this.#manageTestVariants(elements);
    }
  }
}

export default ABTesting;
